import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { usePagination, SearchBar } from '../components/Pagination';
import { exportCSV, exportPDF } from '../utils/export.helpers';

export default function Classements() {
  const [tab, setTab] = useState('classements');
  return (
    <>
      <div className="tabs">
        <button className={`tab ${tab === 'classements' ? 'active' : ''}`} onClick={() => setTab('classements')}>Classements</button>
        <button className={`tab ${tab === 'moyennes' ? 'active' : ''}`} onClick={() => setTab('moyennes')}>Moyennes générales</button>
        <button className={`tab ${tab === 'taux' ? 'active' : ''}`} onClick={() => setTab('taux')}>Taux de réussite</button>
      </div>
      {tab === 'classements' && <ClassementParClasse />}
      {tab === 'moyennes'    && <MoyennesGenerales />}
      {tab === 'taux'        && <TauxReussite />}
    </>
  );
}

function ClassementParClasse() {
  const [classes, setClasses]     = useState([]);
  const [periods, setPeriods]     = useState([]);
  const [classId, setClassId]     = useState('');
  const [periodId, setPeriodId]   = useState('');
  const [rows, setRows]           = useState([]);
  const [error, setError]         = useState('');
  const [loading, setLoading]     = useState(false);

  useEffect(() => {
    api.get('/catalog/classes').then((c) => { setClasses(c || []); if (c?.[0]) setClassId(c[0].id); }).catch(() => {});
    api.get('/catalog/periods').then((p) => { setPeriods(p || []); if (p?.[0]) setPeriodId(p[0].id); }).catch(() => {});
  }, []);

  useEffect(() => { if (classId) load(); }, [classId, periodId]);

  function load() {
    setLoading(true);
    const qs = [`classId=${classId}`, periodId ? `periodId=${periodId}` : ''].filter(Boolean).join('&');
    api.get(`/evaluations/rankings?${qs}`).then(setRows)
    .catch(() => {
      // Fallback: calculer depuis les évaluations
      api.get(`/evaluations?classId=${classId}`).then((evals) => {
        const studentMap = {};
        (evals || []).forEach((ev) => {
          (ev.results || []).forEach((r) => {
            if (!studentMap[r.studentId]) studentMap[r.studentId] = { studentId: r.studentId, name: r.student ? `${r.student.lastName} ${r.student.firstName}` : '—', matricule: r.student?.matricule || '—', notes: [], absences: 0 };
            if (!r.absent && r.totalObtained != null) studentMap[r.studentId].notes.push({ note: r.totalObtained, coeff: ev.coefficient || 1, bareme: ev.baremeTotal || 20 });
            if (r.absent) studentMap[r.studentId].absences++;
          });
        });
        const ranked = Object.values(studentMap).map((s) => {
          const totalPoints = s.notes.reduce((sum, n) => sum + (n.note / n.bareme) * 20 * n.coeff, 0);
          const totalCoeff = s.notes.reduce((sum, n) => sum + n.coeff, 0);
          const moyenne = totalCoeff > 0 ? (totalPoints / totalCoeff).toFixed(2) : null;
          return { ...s, moyenne, totalCoeff };
        }).filter((s) => s.moyenne !== null)
          .sort((a, b) => Number(b.moyenne) - Number(a.moyenne))
          .map((s, i) => ({ ...s, rang: i + 1 }));
        setRows(ranked);
      }).catch((e) => setError(e.message));
    }).finally(() => setLoading(false));
  }

  const mention = (m) => {
    const n = Number(m);
    if (n >= 18) return { l: 'Excellent',    c: '#16a34a' };
    if (n >= 16) return { l: 'Très bien',    c: '#22c55e' };
    if (n >= 14) return { l: 'Bien',         c: '#84cc16' };
    if (n >= 12) return { l: 'Assez bien',   c: '#eab308' };
    if (n >= 10) return { l: 'Passable',     c: '#f97316' };
    return               { l: 'Insuffisant', c: '#dc2626' };
  };

  function doExportCSV() {
    exportCSV(rows.map((r) => ({ Rang: r.rang, Matricule: r.matricule, 'Nom & Prénom': r.name, Moyenne: r.moyenne, Mention: mention(r.moyenne).l, Absences: r.absences })), 'classement');
  }

  function doExportPDF() {
    const cls = classes.find((c) => c.id === classId);
    exportPDF(`Classement — ${cls?.name || ''} — ${periods.find((p) => p.id === periodId)?.name || ''}`,
      ['Rang', 'Matricule', 'Nom & Prénom', 'Moyenne /20', 'Mention', 'Absences'],
      rows.map((r) => [r.rang, r.matricule, r.name, r.moyenne, mention(r.moyenne).l, r.absences]),
      'classement'
    );
  }

  const moy = rows.length ? (rows.reduce((s, r) => s + Number(r.moyenne), 0) / rows.length).toFixed(2) : null;
  const max = rows[0]?.moyenne || null;
  const min = rows[rows.length - 1]?.moyenne || null;
  const taux = rows.length ? Math.round(rows.filter((r) => Number(r.moyenne) >= 10).length / rows.length * 100) : 0;


  const { paginated: rankPaginated, search: rkSearch, setSearch: setRkSearch, Pagination: RkPagination, total: rkTotal, filtered: rkFiltered } = usePagination(rows, { pageSize: 5, searchKeys: ['name', 'matricule', 'moyenne'] });
  return (
    <>
      {moy && (
        <div className="kpi-grid" style={{ marginBottom: 16 }}>
          <div className="kpi"><span>Moyenne classe</span><strong>{moy}/20</strong></div>
          <div className="kpi"><span>Note minimale</span><strong>{min}/20</strong></div>
          <div className="kpi"><span>Note maximale</span><strong>{max}/20</strong></div>
          <div className="kpi green"><span>Taux de réussite</span><strong style={{ color: taux >= 50 ? 'var(--success)' : 'var(--danger)' }}>{taux} %</strong></div>
        </div>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Classement par classe</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} style={{ width: 150 }}>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} style={{ width: 130 }}>
              <option value="">Tous trimestres</option>
              {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button className="btn-ghost btn-sm" onClick={doExportCSV}>↓ Excel</button>
            <button className="btn-ghost btn-sm" onClick={doExportPDF}>↓ PDF</button>
          </div>
        </div>
        <div className="card-body">
          {error && <div className="form-error">{error}</div>}
          <SearchBar value={rkSearch} onChange={setRkSearch} placeholder="Nom, matricule…" total={rkTotal} filtered={rkFiltered.length} />
            {loading ? <div className="loading">Calcul en cours…</div> : rkFiltered.length === 0 ? (
            <div className="empty">Aucune note saisie pour cette classe.</div>
          ) : (
            <div className="table-wrap"><table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Rang</th>
                  <th>Matricule</th>
                  <th>Nom & Prénom</th>
                  <th className="num">Moyenne /20</th>
                  <th>Mention</th>
                  <th className="num">Absences</th>
                </tr>
              </thead>
              <tbody>{rankPaginated.map((r) => {
                const { l, c } = mention(r.moyenne);
                return (
                  <tr key={r.studentId} style={r.rang <= 3 ? { background: 'var(--surface-soft)' } : {}}>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>
                      {r.rang === 1 ? '🥇' : r.rang === 2 ? '🥈' : r.rang === 3 ? '🥉' : r.rang}
                    </td>
                    <td className="mono">{r.matricule}</td>
                    <td><b>{r.name}</b></td>
                    <td className="num" style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '1rem' }}>{r.moyenne}</td>
                    <td>
                      <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: c + '20', color: c }}>{l}</span>
                    </td>
                    <td className="num">{r.absences}</td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          )}
        </div>
      </div>
    </>
  );
}

function MoyennesGenerales() {
  const [classes, setClasses] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [data, setData]       = useState([]);
  const [periodId, setPeriodId] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/catalog/classes').then(setClasses).catch(() => {});
    api.get('/catalog/periods').then((p) => { setPeriods(p || []); if (p?.[0]) setPeriodId(p[0].id); }).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [periodId]);

  function load() {
    setLoading(true);
    api.get('/catalog/classes').then(async (classes) => {
      const results = await Promise.all((classes || []).map(async (cls) => {
        try {
          const evals = await api.get(`/evaluations?classId=${cls.id}`);
          const notes = (evals || []).flatMap((ev) =>
            (ev.results || []).filter((r) => !r.absent && r.totalObtained != null)
              .map((r) => ({ note: r.totalObtained, bareme: ev.baremeTotal || 20, coeff: ev.coefficient || 1 }))
          );
          const moy = notes.length ? (notes.reduce((s, n) => s + (n.note / n.bareme) * 20, 0) / notes.length).toFixed(2) : null;
          const eleves = new Set((evals || []).flatMap((ev) => (ev.results || []).map((r) => r.studentId))).size;
          const taux = notes.length ? Math.round(notes.filter((n) => (n.note / n.bareme) * 20 >= 10).length / notes.length * 100) : 0;
          return { classe: cls.name, eleves, moyenne: moy, taux, nbNotes: notes.length };
        } catch { return { classe: cls.name, eleves: 0, moyenne: null, taux: 0, nbNotes: 0 }; }
      }));
      setData(results.filter((r) => r.moyenne !== null).sort((a, b) => Number(b.moyenne) - Number(a.moyenne)));
    }).catch(() => {}).finally(() => setLoading(false));
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Moyennes générales par classe</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={periodId} onChange={(e) => setPeriodId(e.target.value)} style={{ width: 140 }}>
            <option value="">Tous trimestres</option>
            {periods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <button className="btn-ghost btn-sm" onClick={load}>Actualiser</button>
        </div>
      </div>
      <div className="card-body">
        {loading ? <div className="loading">Calcul…</div> : data.length === 0 ? <div className="empty">Aucune donnée.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Classe</th><th className="num">Élèves</th><th className="num">Moyenne /20</th><th className="num">Taux réussite</th><th className="num">Notes saisies</th></tr></thead>
            <tbody>{data.map((d, i) => (
              <tr key={i}>
                <td><b>{d.classe}</b></td>
                <td className="num">{d.eleves}</td>
                <td className="num" style={{ fontFamily: 'monospace', fontWeight: 700, color: Number(d.moyenne) >= 10 ? 'var(--success)' : 'var(--danger)' }}>{d.moyenne}</td>
                <td className="num">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                    <div style={{ width: 60, height: 6, background: 'var(--line)', borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${d.taux}%`, background: d.taux >= 50 ? 'var(--success)' : 'var(--danger)', borderRadius: 3 }} />
                    </div>
                    <span>{d.taux}%</span>
                  </div>
                </td>
                <td className="num">{d.nbNotes}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function TauxReussite() {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/catalog/classes').then(setClasses).catch(() => {});
    api.get('/catalog/subjects').then(setSubjects).catch(() => {});
    api.get('/evaluations').then((evals) => {
      const subjectMap = {};
      (evals || []).forEach((ev) => {
        const key = ev.subjectId;
        if (!subjectMap[key]) subjectMap[key] = { name: ev.subject?.name || '—', notes: [], evalCount: 0 };
        subjectMap[key].evalCount++;
        (ev.results || []).forEach((r) => {
          if (!r.absent && r.totalObtained != null) {
            subjectMap[key].notes.push((r.totalObtained / (ev.baremeTotal || 20)) * 20);
          }
        });
      });
      const rows = Object.values(subjectMap).map((s) => ({
        name: s.name,
        evalCount: s.evalCount,
        moyenne: s.notes.length ? (s.notes.reduce((a, b) => a + b, 0) / s.notes.length).toFixed(2) : null,
        taux: s.notes.length ? Math.round(s.notes.filter((n) => n >= 10).length / s.notes.length * 100) : 0,
        nbNotes: s.notes.length,
      })).filter((r) => r.moyenne !== null).sort((a, b) => Number(b.taux) - Number(a.taux));
      setData(rows);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="card">
      <div className="card-head"><h2>Taux de réussite par matière</h2></div>
      <div className="card-body">
        {loading ? <div className="loading">Calcul…</div> : data.length === 0 ? <div className="empty">Aucune donnée.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Matière</th><th className="num">Évaluations</th><th className="num">Moyenne /20</th><th className="num">Notes saisies</th><th style={{ width: 200 }}>Taux réussite</th></tr></thead>
            <tbody>{data.map((d, i) => (
              <tr key={i}>
                <td><b>{d.name}</b></td>
                <td className="num">{d.evalCount}</td>
                <td className="num" style={{ fontFamily: 'monospace', fontWeight: 700 }}>{d.moyenne}</td>
                <td className="num">{d.nbNotes}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, height: 8, background: 'var(--line)', borderRadius: 4 }}>
                      <div style={{ height: '100%', width: `${d.taux}%`, background: d.taux >= 70 ? 'var(--success)' : d.taux >= 50 ? 'var(--warn)' : 'var(--danger)', borderRadius: 4 }} />
                    </div>
                    <span style={{ minWidth: 35, fontWeight: 600, color: d.taux >= 70 ? 'var(--success)' : d.taux >= 50 ? 'var(--warn)' : 'var(--danger)' }}>{d.taux}%</span>
                  </div>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
